import type { ChecklistStatus } from '../../shared/checklistUtils';
import { getStatusLabel } from '../../shared/format';

import type { CampaignChecklistDataPoint, CampaignChecklistRow } from './types';

export function buildCampaignRow({
  action,
  approvalNote,
  approved,
  dataPoints,
  evidence,
  field,
  id,
  packageName,
  policyBibleRule,
  rule,
  source,
  summary,
  value,
}: Omit<CampaignChecklistRow, 'status' | 'statusLabel' | 'dataPoints' | 'summary'> & {
  approved: boolean;
  dataPoints?: Array<CampaignChecklistDataPoint>;
  summary?: string;
}): CampaignChecklistRow {
  const status: ChecklistStatus = approved ? 'approved' : 'requires approval';

  return {
    action,
    approvalNote,
    dataPoints: dataPoints ?? [{ label: 'Loaded value', value }],
    evidence,
    field,
    id,
    packageName,
    policyBibleRule,
    rule,
    source,
    status,
    statusLabel: getStatusLabel(status),
    summary: summary ?? getCampaignSummary(field, approved),
    value,
  };
}

export function formatStringValue(value?: string | number | boolean | null, fallback = 'Not available') {
  if (value == null || value === '') {
    return fallback;
  }

  return String(value);
}

function getCampaignSummary(field: CampaignChecklistRow['field'], approved: boolean) {
  switch (field) {
    case 'Date change allowed':
      return approved
        ? 'Date change allowance matches the policy bible.'
        : 'Date change allowance needs policy bible review.';
    case 'Days prior to check-in for date changes':
      return approved
        ? 'Days prior to check-in for date changes matches policy bible.'
        : 'Days prior to check-in for date changes needs policy bible review.';
    case 'Number of date changes':
      return approved
        ? 'Number of permitted date changes matches the policy bible.'
        : 'Number of permitted date changes needs policy bible review.';
    case 'Booking data cut-off':
      return approved
        ? 'Booking data cut-off is populated.'
        : 'Booking data cut-off is blank and should be populated to 2.';
    case 'Package upgrades allowed':
      return approved ? 'Package upgrades are allowed.' : 'Package upgrades need review.';
    case 'SEM support':
      return approved ? 'SEM support is not enabled.' : 'SEM support is enabled.';
    case 'Campaign type':
      return approved ? 'Campaign type is a supported Flash type.' : 'Campaign type needs review.';
    case 'Contract Terms Modification':
      return approved
        ? 'Contract terms modification field is blank.'
        : 'Contract terms modification field is populated.';
    case 'Payment Terms Modification':
      return approved ? 'Payment Terms Modification is blank.' : 'Payment Terms Modification is enabled.';
    case 'Book By date':
      return approved ? 'Book By is approximately one month before Travel To.' : 'Book By date needs review.';
    case 'Suggested value':
      return approved
        ? 'Suggested value equals value in cost currency for every deal option.'
        : 'Suggested value needs review.';
    default:
      return approved ? 'Campaign row is approved.' : 'Campaign row needs review.';
  }
}
